import axios from 'axios';

import userStore, { USER_TOKEN } from '@/stores/user';
import { message } from 'antd';
import isMobile from 'is-mobile';
import rum, { EReportModule } from '@/plugins/rum';

/**
 * 判断是否需要跳转登录页。
 * @returns {boolean} 当前页面是否应跳转到登录。
 */
const judgeIsJumpLogin = () => {
  const url = window?.location?.pathname || '';

  // 登录页
  if (url.includes('/login')) {
    return false;
  }

  // 免登的对话详情页
  const botDetail = /^\/outer\/bot(?:\/([^\/]+))?\/?$/i;
  if (botDetail.test(url)) {
    return false;
  }

  return true;
};

/**
 * 识别整批导入接口，避免把文件、正文和完整结果写入遥测。
 * @param {string | undefined} url 当前请求地址。
 * @returns {boolean} 是否为新增整批导入接口。
 */
const isArticleImportRequest = (url?: string): boolean => /(?:^|\/)company\/article\/upload\/(?:rules|batches)(?:\/|$)/.test((url || '').split('?')[0]);

/**
 * 为新导入接口提取固定安全字段，不输出请求或完整响应。
 * @param {string | undefined} url 当前接口地址。
 * @param {{log_id?: string, err_no?: number, data?: {errorCode?: string}}} response 业务响应。
 * @returns {Record<string, unknown>} 仅 URL、Trace ID 和安全错误分类。
 */
const importTelemetry = (url: string | undefined, response?: { log_id?: string; err_no?: number; data?: { errorCode?: string } }): Record<string, unknown> => ({
  url: (url || '').split('?')[0],
  traceId: response?.log_id,
  errorCode: /^[A-Z][A-Z0-9_]{0,63}$/.test(response?.data?.errorCode || '') ? response?.data?.errorCode : String(response?.err_no ?? 'QUERY_ERROR'),
});

/**
 * 识别公开报告回答接口，所有错误只能上报白名单字段。
 * @param {string | undefined} url 当前请求地址。
 * @returns {boolean} 是否为回答详情请求。
 */
const isReportAnswerRequest = (url?: string): boolean => /(?:^|\/)geo\/task\/run-record\/analysis-answer$/.test((url || '').split('?')[0]);

/**
 * 提取回答错误的安全上下文，禁止原始错误、请求参数或响应正文进入遥测。
 * @param {{log_id?: string} | undefined} response 业务响应。
 * @param {'BUSINESS_ERROR' | 'NETWORK_ERROR'} errorCode 固定安全分类。
 * @returns {Record<string, unknown>} 仅固定接口路径、Trace ID 与安全分类。
 */
const reportAnswerTelemetry = (response: { log_id?: string } | undefined, errorCode: 'BUSINESS_ERROR' | 'NETWORK_ERROR'): Record<string, unknown> => ({
  url: '/geo/task/run-record/analysis-answer',
  traceId: response?.log_id,
  errorCode,
});

const service = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'X-client-type': isMobile() ? 'MOBILE' : 'PC',
  }
});

service.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
service.interceptors.response.use(
  /**
   * 解包业务响应，并为导入和公开报告回答错误生成脱敏遥测。
   * @param {import('axios').AxiosResponse} res 原始 HTTP 响应。
   * @returns {Promise<unknown>} 业务 data；失败时拒绝并交给调用方处理。
   */
  async (res) => {
    const { isSilence = false, responseType } = res.config;
    // 直接返回二进制
    if (responseType === 'blob') {
      return res.data;
    }
    // 公开报告接口保留业务拒绝语义，避免登录跳转和完整正文遥测。
    if (isReportAnswerRequest(res.config.url) && res.data.err_no !== 0) {
      rum.report?.error({ module: EReportModule.Api, keyword: '请求错误', extra: reportAnswerTelemetry(res.data, 'BUSINESS_ERROR') });
      return Promise.reject({ err_no: res.data.err_no, log_id: res.data.log_id });
    }
    if (res.data.err_no === 10005 && window.location.pathname !== '/login') {
      userStore.setUserInfo(null);
      if (judgeIsJumpLogin()) {
        window.location.href = `/login?redirect=${encodeURIComponent(location.href)}`;
      }
      return Promise.reject(res.data?.err_msg || '登录已过期');
    }
    if (res.data.err_no !== 0) {
      // 报错统一提示
      if (!isSilence) {
        message.warning(res.data.err_msg || '服务器开小差了, 请稍后再试!');
      }
      rum.report?.error({
        module: EReportModule.Api,
        keyword: '请求错误',
        extra: isArticleImportRequest(res.config.url) ? importTelemetry(res.config.url, res.data) : { url: res.config.url, request: res.config.data, response: res.data }
      });
      return Promise.reject(res.data);
    }
    return res.data.data;
  },
  /**
   * 处理传输异常及登录过期，导入和回答接口仅记录安全字段。
   * @param {import('axios').AxiosError} error HTTP 或网络异常。
   * @returns {Promise<never>} 保留导入保存前拒绝业务码，其他异常交给业务流程判断是否需要核实。
   */
  async (error) => {
    const { isSilence = false } = error.config || {};
    if (isReportAnswerRequest(error.config?.url)) {
      rum.report?.error({ module: EReportModule.Api, keyword: '请求错误', extra: reportAnswerTelemetry(error.response?.data, 'NETWORK_ERROR') });
      // HTTP 错误若附带明确业务码，弹窗仍要求刷新；不向调用方传递原始响应正文。
      return Promise.reject(error.response?.data?.err_no !== undefined
        ? { err_no: error.response.data.err_no, log_id: error.response.data.log_id }
        : new Error('REPORT_ANSWER_NETWORK_ERROR'));
    }
    if (isArticleImportRequest(error.config?.url)) {
      rum.report?.error({ module: EReportModule.Api, keyword: '请求错误', extra: importTelemetry(error.config?.url, error.response?.data) });
    }
    // 导入10004仅表示任何保存前拒绝，HTTP状态不改变其含义；通用异常仍保留未知语义。
    if (isArticleImportRequest(error.config?.url) && error.response?.data?.err_no === 10004) {
      if (!isSilence) {
        message.warning(error.response.data.err_msg || '服务器开小差了, 请稍后再试!');
      }
      return Promise.reject(error.response.data);
    }
    if (error?.response?.status === 401) {
      // 已登录过则提示
      if (localStorage.getItem(USER_TOKEN)) {
        message.warning('登录已过期');
        if (judgeIsJumpLogin()) {
          setTimeout(() => {
            window.location.href = `/login?redirect=${encodeURIComponent(location.href)}`;
          }, 300);
        }
      }
      userStore.setUserInfo(null);
      return Promise.reject(error);
    }
    // 报错统一提示
    if (!isSilence) {
      message.warning(error?.response?.data?.err_msg || '服务器开小差了, 请稍后再试!');
    }
    return Promise.reject(error);
  }
);

export default service;
